import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { useChildTokens } from '@/components/child/useChildTokens';

/**
 * Barnvänlig pratbubbla för kompanjonkaraktären (NPF-barnvyn).
 *
 * Ren presentationskomponent: rundad, mjuk skugga, liten pil som pekar mot
 * karaktären. Använder temats färger (childMode: primary.50/100, grey,
 * Nunito-typografi med line-height 1.6). Läsbar minst 15px enligt
 * designförslagets pratbubbla-spec (>= 14px-kravet).
 *
 * Pilens placering styrs av 'pointer' så att bubblan kan ligga bredvid eller
 * under figuren beroende på layout (mobil: bredvid, desktop: under).
 */

export type SpeechBubblePointer = 'left' | 'bottom' | 'top' | 'none';

export type SpeechBubbleProps = {
  /** Pratbubblans innehåll. Ges som text-prop eller children (children vinner). */
  text?: string;
  children?: ReactNode;
  /** Var pilen pekar mot karaktären. Default 'left'. */
  pointer?: SpeechBubblePointer;
  /**
   * 'polite' (default) annonserar textändringar via aria-live utan att avbryta.
   * Sätt 'off' om bubblan är rent dekorativ.
   */
  ariaLive?: 'polite' | 'off';
  /**
   * Visar en liten statisk högtalar-ikon i bubblans nedre högra hörn som
   * affordans för att bubblan kan läsas upp (tap på avataren). Default false så
   * befintliga anrop inte påverkas.
   */
  speakable?: boolean;
};

// Pil som matchar bubblans bakgrund/border. Renderas som roterad kvadrat
// (CSS-only, ingen extra asset) positionerad mot karaktärens sida.
const pointerStyles: Record<
  Exclude<SpeechBubblePointer, 'none'>,
  Record<string, string | number>
> = {
  left: {
    left: -7,
    top: 22,
    borderTop: 'none',
    borderRight: 'none',
  },
  bottom: {
    bottom: -7,
    left: 28,
    borderTop: 'none',
    borderLeft: 'none',
  },
  top: {
    top: -7,
    left: 28,
    borderBottom: 'none',
    borderRight: 'none',
  },
};

const SpeechBubble = ({
  text,
  children,
  pointer = 'left',
  ariaLive = 'polite',
  speakable = false,
}: SpeechBubbleProps) => {
  // Skuggan följer temats bläckfärg i stället för en hårdkodad ljus-hex — en
  // rgba(45,49,66,…)-skugga är osynlig i mörkt läge.
  const { shadow } = useChildTokens();

  return (
    <Box
      aria-live={ariaLive === 'off' ? undefined : ariaLive}
      sx={{
        position: 'relative',
        display: 'inline-block',
        maxWidth: { xs: 200, sm: 240 },
        bgcolor: 'background.paper',
        border: '1.5px solid',
        borderColor: 'grey.300',
        borderRadius: '16px',
        px: 2,
        py: 1.25,
        boxShadow: shadow,
        // Pilen: en liten roterad ruta som ärver bubblans bakgrund + border.
        ...(pointer !== 'none' && {
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 14,
            height: 14,
            bgcolor: 'background.paper',
            border: '1.5px solid',
            borderColor: 'grey.300',
            transform: 'rotate(45deg)',
            ...pointerStyles[pointer],
          },
        }),
      }}
    >
      <Typography
        variant="body1"
        sx={{
          position: 'relative',
          zIndex: 1,
          fontSize: 15,
          fontWeight: 600,
          lineHeight: 1.6,
          color: 'text.primary',
          m: 0,
          // Ge plats åt den statiska ljud-ikonen i nedre högra hörnet.
          ...(speakable && { pr: 2 }),
        }}
      >
        {children ?? text}
      </Typography>
      {speakable && (
        <VolumeUpRoundedIcon
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 8,
            fontSize: 16,
            color: 'primary.main',
            opacity: 0.6,
          }}
        />
      )}
    </Box>
  );
};

export default SpeechBubble;
